<template>
  <div class="flex flex-col items-center gap-[12px]" data-testid="one-time-qr">
    <!-- Level M, not the static QR's H: the ~146-character token at H is a
         version-12 code, ~3.5px a module at 230px; at M it is version 7, ~5px,
         which a scanner reads far more reliably off a phone screen. -->
    <qrcode-vue v-if="showQr" :value="token" :size="230" level="M" data-testid="one-time-qr-code" />
    <div
      v-else-if="offline"
      class="w-[230px] h-[230px] flex flex-col items-center justify-center gap-[16px] text-center"
      role="alert"
      data-testid="one-time-qr-offline"
    >
      <p class="m-0 text-[1rem] leading-[1.5rem] font-medium text-grey-800">
        Connect to the internet to show your QR code.
      </p>
      <button
        type="button"
        class="py-[12px] px-[24px] rounded-[100px] bg-transparent border-solid border-[2px] border-grey-800 text-[1rem] leading-[1.5rem] font-bold text-grey-800 tracking-[-0.48px]"
        data-testid="one-time-qr-retry"
        @click="refresh"
      >
        Try again
      </button>
    </div>
    <div
      v-else
      class="w-[230px] h-[230px] flex items-center justify-center text-grey-800"
      data-testid="one-time-qr-loading"
    >
      Loading...
    </div>
    <!-- Counts to the next swap, not to expiry: the app refreshes every 60s and
         the API accepts a code for 75s, so a scan in the last second still has
         15s to spare. Only
         when a refresh fails does it count the real expiry. role="timer" with
         aria-live off, so a screen reader is not read every second. -->
    <p
      v-if="showQr"
      role="timer"
      aria-live="off"
      class="m-0 text-center text-[0.875rem] leading-[1.25rem] text-prime-muted tabular-nums"
      data-testid="one-time-qr-countdown"
    >
      {{ countdownText }}
    </p>
  </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue';
import {
  QR_TOKEN_DEFAULT_REFRESH_SECONDS,
  QR_TOKEN_RATE_LIMITED_RETRY_MS,
  QR_TOKEN_RETRY_MS,
} from '@/constants/scan-print';
import { SCAN_PRINT_ERRORS, fetchQrToken } from '@/utils/scan-print-api';

/**
 * My QR's short-lived token. The API mints each token for 75 seconds (it can be
 * scanned any number of times until then)
 * and says when to fetch the next (`refreshAfterSeconds`, 60 today). This asks
 * for one on mount, again on that schedule, and at once when the app comes
 * back to the foreground; it stops while the app is hidden and when My QR
 * unmounts. A token is never shown past its `expiresAt`: offline, the QR gives
 * way to a "connect" message instead of a code that would only be refused.
 * A 404 means the flag is off for this member on the server: `unavailable`
 * tells My QR to show the static QR instead.
 */
export default {
  name: 'OneTimeQrCode',
  components: { QrcodeVue },
  emits: ['unavailable'],
  data() {
    return {
      token: null,
      expiresAtMs: 0,
      offline: false,
      timer: null,
      expiryTimer: null,
      inFlight: false,
      stopped: false,
      nextRefreshAtMs: 0,
      refreshFailed: false,
      // Set by a 429: no automatic ask before this moment, foreground included.
      rateLimitedUntilMs: 0,
      nowMs: Date.now(),
      ticker: null,
    };
  },
  computed: {
    showQr() {
      return Boolean(this.token) && !this.offline;
    },
    countdownText() {
      const until = this.refreshFailed ? this.expiresAtMs : this.nextRefreshAtMs;
      // Never 0: at 0 the next code is already on its way.
      const seconds = Math.max(Math.ceil((until - this.nowMs) / 1000), 1);
      return this.refreshFailed
        ? `This QR code will expire in ${seconds}s.`
        : `This QR code will automatically refresh in ${seconds}s.`;
    },
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    if (!document.hidden) {
      this.startTicker();
      this.refresh();
    }
  },
  beforeUnmount() {
    this.stopped = true;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.clearTimers();
    this.stopTicker();
  },
  methods: {
    onVisibilityChange() {
      if (document.hidden) {
        this.clearTimers();
        this.stopTicker();
      } else {
        this.startTicker();
        // Coming back must not cut a 429 back-off short.
        const backOffLeft = this.rateLimitedUntilMs - Date.now();
        if (backOffLeft > 0) this.schedule(backOffLeft);
        else this.refresh();
      }
    },
    /** One tick a second drives the countdown; nothing runs while the app is hidden. */
    startTicker() {
      this.stopTicker();
      this.nowMs = Date.now();
      this.ticker = setInterval(() => {
        this.nowMs = Date.now();
      }, 1000);
    },
    stopTicker() {
      clearInterval(this.ticker);
      this.ticker = null;
    },
    async refresh() {
      if (this.inFlight || this.stopped) return;
      this.inFlight = true;
      this.clearTimers();
      try {
        const minted = await fetchQrToken({ token: this.$store.state.user.token });
        if (this.stopped) return;
        this.token = minted.token;
        this.expiresAtMs = Date.parse(minted.expiresAt);
        this.offline = false;
        this.refreshFailed = false;
        this.rateLimitedUntilMs = 0;
        const seconds = minted.refreshAfterSeconds ?? QR_TOKEN_DEFAULT_REFRESH_SECONDS;
        this.nextRefreshAtMs = Date.now() + seconds * 1000;
        this.nowMs = Date.now();
        this.schedule(seconds * 1000);
        this.expiryTimer = setTimeout(this.expire, Math.max(this.expiresAtMs - Date.now(), 0));
      } catch (error) {
        if (this.stopped) return;
        if (error.code === SCAN_PRINT_ERRORS.UNAVAILABLE) {
          this.$emit('unavailable');
          return;
        }
        // The last token may still be good for a few seconds: keep it, but only
        // until the moment it expires, whatever the retries are doing.
        const left = this.expiresAtMs - Date.now();
        if (!this.token || left <= 0) this.expire();
        else {
          this.refreshFailed = true;
          this.expiryTimer = setTimeout(this.expire, left);
        }
        if (error.code === SCAN_PRINT_ERRORS.RATE_LIMITED) {
          this.rateLimitedUntilMs = Date.now() + QR_TOKEN_RATE_LIMITED_RETRY_MS;
          this.schedule(QR_TOKEN_RATE_LIMITED_RETRY_MS);
        } else {
          this.schedule(QR_TOKEN_RETRY_MS);
        }
      } finally {
        this.inFlight = false;
      }
    },
    expire() {
      this.token = null;
      this.offline = true;
    },
    schedule(ms) {
      if (document.hidden || this.stopped) return;
      this.timer = setTimeout(this.refresh, ms);
    },
    clearTimers() {
      clearTimeout(this.timer);
      clearTimeout(this.expiryTimer);
      this.timer = null;
      this.expiryTimer = null;
    },
  },
};
</script>
